import type { Node } from "@babylonjs/core/node";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";

import type { TraceScene } from "../core/TraceScene";
import type { SceneObject } from "../core/SceneObject";
import type { ZipProgressCallback } from "./zip/zipWriter";
import { TraceExport, LiveNodeBake, preparedAssetKey, objectNeedsBake, serializeNodesToGlb, showBaseForBake } from "./TraceExport";
import { MATERIAL_SETTLE_TIMEOUT_MS } from "../behaviors/renderer";
import { awaitLayoutQuiescence } from "./settle";
import { PRIMITIVE_FILENAMES } from "./BuiltInPrimitives";
import { Logger } from "../utils/Logger";
import { DROPPABLE_ASSET_EXTENSIONS, isWebPageNotAsset } from "../utils/AssetTypes";
import { isSiteAbsolutePath } from "../utils/assetPaths";
import { isEditorHelperMesh, warnHelperMeshExcluded } from "./exportFilters";
import { validatePublishableScene } from "./validatePublishableScene";
import { dropImportEraReferenceCopies } from "./referenceCopyMigration";
import { getSceneFormatConfig } from "../loaders/plugins/SceneFormatRegistry";
import { fileExtensionFromUri } from "../loaders/formats/gltf21/normalize";
import { zipEntryNames } from "../loaders/formats/bundle/zipSniff";
import { disposedError } from "../utils/abort";
import { isLegacySceneFormat } from "../loaders/plugins/sceneFormatExtensions";
import { GLTF_JSON_MIME } from "../loaders/formats/gltf21/types";
import { nameStoredFiles } from "./folderUpload";
import { ASSET_ALREADY_IN_FOLDER } from "../loaders/formats/tracex/types";
import { applySubtreeExclusion, withoutExcluded, type ExcludedSubtrees, type SubtreeExclusionOptions } from "./exportExclusion";
import { assertPropertySheetAssetsHosted, collectExportPropertySheets } from "./propertySheetAssets";
import { inlineGltfExternalAssets } from "./gltfExternalAssets";
import {
    validateManifest,
    isAbsoluteHttpUrl,
    formatManifestOffenders,
    type ManifestEntry,
    type ManifestOffender,
    type ManifestValidationOptions,
} from "./manifestValidator";

export type ZipFormat = 'auto' | 'zip' | 'trace';

export type { ZipProgress, ZipProgressCallback } from './zip/zipWriter';

export interface ExportOptions extends SubtreeExclusionOptions {
    zipFormat?: ZipFormat;
    /**
     * Whether the scene is attached to an image-marker anchor — decides the
     * `.zip` frame conversion. Callers holding the CMS record pass
     * `isSceneAnchored(scene)` (from `trace-viewer/cms`). Left undefined,
     * the runtime `ImageAnchor` behavior is used as a fallback (for no-CMS
     * callers) — it attaches asynchronously, so exports can race it.
     * Contract: docs-internal/ZIP_FRAME_CONTRACT.md.
     */
    imageMarker?: boolean;
    /**
     * Fires after each archive entry is written during ZIP assembly.
     * The archive is built in a Web Worker, so callbacks arrive while the
     * page (and render loop) stay responsive.
     */
    onProgress?: ZipProgressCallback;
}

/** Shared by every glTF document writer (`.gltf`, `.glz`, folder). */
export interface KHRInteractivityExportOptions {
    /**
     * Write the scene's root BehaviorGraph as ratified `KHR_interactivity`, for
     * non-Trace viewers. Defaults to `KHR_INTERACTIVITY_EXPORT_DEFAULT` (off):
     * the graph stays in the root's `extras.trace.behaviors`, which every Trace
     * client runs.
     */
    khrInteractivity?: boolean;
}

/**
 * A `.glz` bundle has no zip/trace format switch — only the shared progress
 * hook, and {@link SubtreeExclusionOptions.exclude}, which every folder and
 * document writer honours the same way: `zip(folder)` writes the folder this
 * option produced, so the bundle omits the excluded subtree and its files too.
 */
export interface BundleExportOptions
    extends SubtreeExclusionOptions, KHRInteractivityExportOptions, Pick<ExportOptions, 'onProgress'> {
    /** Reference policy context, shared with folder saves (CONTENT_MODEL_SPEC §2.5). */
    manifest?: ManifestValidationOptions;
}

/**
 * Options for a STANDALONE document write (`.gltf`, {@link
 * SceneExporter.toGltfBlob}) — no folder, no packaging, so
 * {@link SubtreeExclusionOptions.exclude} is all there is to say.
 */
export interface DocumentExportOptions extends SubtreeExclusionOptions, KHRInteractivityExportOptions {}

/**
 * @deprecated Renamed to {@link BundleExportOptions} — the bundle is `.glz`
 *   now (spec §4).
 */
export type TracexExportOptions = BundleExportOptions;

/**
 * A folder (CONTENT_MODEL_SPEC §2.1) as bytes: the document, plus every file it
 * names keyed by the **relative URI the document uses**.
 *
 * The unit every save shares. A folder-mode scene save uploads `files` through
 * `POST /scenes/{id}/files` and posts `document`; an asset upload posts both
 * together; `.glz` is `zip(folder)`. Nothing here is packaged, so saving a
 * scene whose one changed file is a 4 MB video does not rebuild an archive
 * around it.
 *
 * The URIs are the ones the writer authored (`assets/<name>.<ext>`). A server
 * that content-addresses on upload answers with its own `assets/<hash>.<ext>`;
 * repointing the document at those names is the caller's job, and it is a
 * string swap in `files[]`, not a re-export.
 */
/**
 * Where the folder this export produces is going to land (spec §2.2).
 *
 * The whole difference is what happens to a file the scene loaded **from its
 * own folder over the network** and never changed — the bytes are not in the
 * client at all, only a URL mapping is.
 *
 * - `'same'` (default): the destination folder is the one the scene was loaded
 *   from, so it already holds those bytes. They are NAMED by the document and
 *   reported on {@link SceneFolder.existing}; nothing is fetched and nothing is
 *   uploaded. This is the ordinary save, and skipping that re-upload is the
 *   one cost the folder model exists to remove.
 * - `'other'`: the destination is a DIFFERENT folder — a promotion to a new
 *   asset (§3.5), a `POST /assets` from a folder-loaded scene, a copy. Nothing
 *   is already there, so every relative entry is FETCHED and handed back in
 *   {@link SceneFolder.files}, and `existing` comes back empty.
 *
 * `'other'` is exactly the completeness rule the bundle has always had — an
 * archive must carry every file it names — applied to a folder upload. A
 * caller that guesses wrong in this direction re-uploads bytes; one that
 * guesses wrong in the other direction writes a document naming files the
 * destination folder does not hold, which is a scene that loads empty.
 */
export type FolderDestination = 'same' | 'other';

/**
 * Options for a folder export.
 *
 * `manifest` turns on the client half of the server's document gate (spec
 * §2.5): every `files[]` entry is classified as a folder file, a library
 * reference or supported foreign media, and anything else throws with the
 * offending index and class *before* an upload starts. Pass
 * `{ rejectSceneFolderUrls: true }` for an ASSET export — the closure rule
 * (§3.2) is the one thing the two write paths differ on.
 *
 * `exclude` (inherited, spec §2.9) leaves subtrees out of the document
 * entirely — the option a session uses to keep its editor-only content out of
 * the scene it is saving.
 */
export interface FolderExportOptions extends BundleExportOptions {
    /** See {@link FolderDestination}. Default `'same'`. */
    destination?: FolderDestination;
    /**
     * The URL of the folder this export is being written back INTO, ending in
     * `/` — `…/trace-content/assets/{assetId}/` for an asset row,
     * `…/scenes/{sceneId}/` for a scene. Only meaningful with
     * `destination: 'same'`, which is what it makes true.
     *
     * `destination: 'same'` has always MEANT this and never carried it, and
     * the gap costs a **leaf** (spec §3.1: an asset row whose `Url` is the
     * file itself, wrapped in a document by its first edit). A folder scene's
     * files were never blobs, so "the destination already holds this" falls
     * out of a missing blob; a leaf's bytes were downloaded to be shown, so
     * the exporter cannot tell them from bytes it must upload. Told where the
     * save is going, it can: a file whose source URL is inside this folder is
     * one the folder already holds (§2.2) and arrives on
     * {@link SceneFolder.existing} instead of `files`. So does an emitted
     * file whose bytes this session already stored there
     * (`ResourceManager.recordStoredFiles`); the document names it by its
     * stored uri.
     *
     * Omit it and nothing changes — the bytes travel, as they did before.
     */
    folderUrl?: string;
}

export interface SceneFolder {
    /** The glTF 2.1 document, `model/gltf+json`. */
    document: Blob;
    /** `files[k].uri` → bytes. */
    files: Map<string, Blob>;
    /**
     * Relative URIs the destination folder **already holds** — named by the
     * document, carrying no bytes, and nothing to upload (spec §2.2).
     *
     * A scene opened from its folder loads its files over the network; the
     * bytes never come back to the client as blobs, and re-uploading them on
     * every save is exactly the cost the folder model removes. So a client's
     * save is: upload `files`, skip `existing`, post `document`.
     *
     * `files` and `existing` are disjoint and together cover every relative
     * entry in the document. Absolute entries — library references (§3.4) and
     * foreign streamable media (§3.3) — are in neither: they are nobody's
     * folder files.
     */
    existing: Set<string>;
}

/**
 * What a folder export covers when it is not the whole scene.
 *
 * `roots` is the selection, in author order; `groupRootName` names the
 * structural root the writer SYNTHESIZES over them (SCENE_ROOT_SPEC §4).
 * Synthesized in the exported records only — promotion reads the scene, it
 * never restructures it.
 */
interface SubtreeScope {
    roots: readonly SceneObject[];
    groupRootName?: string;
}

/**
 * Options for {@link SceneExporter.promoteToAsset} (spec §3.5).
 */
export interface PromoteToAssetOptions extends BundleExportOptions {
    /**
     * Manifest classification options (spec §2.5). The closure rule is not one
     * of them: an asset export always rejects scene-folder URLs (§3.2), which
     * is the whole reason promotion exists as its own verb.
     *
     * With no `apiBaseUrl` there is no library-reference class, so every
     * absolute reference in the subtree is judged as foreign media and a
     * library reference comes back an offender. Fail-closed, like the server.
     */
    manifest?: Omit<ManifestValidationOptions, 'rejectSceneFolderUrls'>;
    /**
     * The asset's name. Also the name of the synthesized group root when the
     * promotion covers several objects; defaults to the first root's name.
     */
    name?: string;
    /**
     * Group a single-object promotion under a synthesized root too.
     *
     * Off by default: one object already satisfies the single-root contract,
     * and wrapping it would add a level the author never made and change the
     * handle a host receives on placement.
     */
    alwaysGroup?: boolean;
}

/**
 * The bytes a "Save as asset" needs, and the reasons it might not be allowed.
 *
 * `offenders` is REPORTED, not thrown: the editor shows the author which
 * `files[]` entries the closure rule refuses and which node put them there,
 * and a promotion with offenders is a promotion the server would refuse — so
 * a caller must check it before uploading.
 */
export interface PromotedAsset {
    /** The glTF 2.1 document, `model/gltf+json`. */
    document: Blob;
    /** Every file that travels into the new asset folder, keyed by its relative uri. */
    files: Map<string, Blob>;
    /** Manifest entries the asset closure rule refuses (§3.2). Empty is the good case. */
    offenders: ManifestOffender[];
}

/** Surface incomplete exports without claiming unresolved references were preserved. */
function reportExportWarnings(format: string, sceneName: string, warnings: readonly string[] | undefined): void {
    if (!warnings?.length) return;
    Logger.warn(
        `[SceneExporter] ${format} export of "${sceneName}" completed with ${warnings.length} unresolved reference(s); `
        + warnings.join('; '),
    );
}

/**
 * Which written artifacts carry baked geometry.
 *
 * An ASSET does double duty — a dumb glTF client renders it, and the editor
 * re-opens it through the provenance saved beside it — and there is no
 * edit-only asset kind, so an asset package always carries the baked shape.
 * `.trace` and `.glz` differ in document format, not in kind: both are
 * packed packages, so both bake. A single-object `.glb` is an asset too.
 *
 * The legacy `.zip` SCENE bakes as well. Its objects used to reference assets
 * that had already baked, but a placed component is now a copy of the asset
 * and an authoring load re-opens it as the live rig, so a scene holds rigs.
 * The Unity app reads `.zip` and runs no MorphStretch, MeshBackground or
 * Bevel, so an unbaked scene shows every resized component at rest.
 * `Provenance.bakedFrom` keeps the way back, as it does for an asset.
 *
 * A standalone `.gltf` document cannot bake by nature — it is references
 * only. (`'glxf'` is accepted as the legacy spelling of that case; nothing
 * writes it.)
 *
 * Not an option a caller passes: the artifact decides.
 */
export function bakesFor(format: 'trace' | 'glz' | 'tracex' | 'zip' | 'gltf' | 'glxf'): boolean {
    return format === 'trace' || format === 'glz' || format === 'tracex' || format === 'zip';
}

/**
 * The `files[]` manifest of a written document.
 *
 * Read back out of the produced bytes rather than kept alongside them, so what
 * the validator judges is exactly what the upload sends — the same document,
 * not a parallel record of it that could drift.
 */
async function readManifestEntries(document: Blob): Promise<ManifestEntry[]> {
    const parsed = JSON.parse(await document.text()) as { files?: ManifestEntry[] };
    return parsed.files ?? [];
}

/** One export at a time per scene — see `SceneExporter.settled`. */
const WRITE_QUEUE = new WeakMap<TraceScene, Promise<void>>();

/**
 * Orchestrates exporting a TraceScene (or subtree) into various formats.
 *
 * All methods return Blobs; the caller is responsible for triggering a
 * download (or doing whatever else with the bytes).
 */
export class SceneExporter {
    constructor(private readonly traceScene: TraceScene) {}

    /**
     * #664: every writer here captures a POSE. Layout passes are
     * microtask-batched, so a capture taken mid-solve freezes a
     * pre-final arrangement into the file.
     *
     * The gate lives on the entry points, once, rather than as a copy inside
     * each writer — a fourth writer forgetting its copy is exactly how
     * `toGlxfBlob` shipped without one.
     */
    private async settled<T>(write: () => Promise<T>): Promise<T> {
        // …and they run ONE AT A TIME per scene. A bake mutates live meshes
        // onto throwaway geometry and restores them in a `finally`; two
        // overlapping exports interleave those windows, so the second
        // snapshots the first's throwaway geometry and the first's restore
        // then disposes what the second is standing on. The result is a live
        // mesh bound to a disposed Geometry — silent, and only in the editor
        // that did it, since the written file looks fine.
        //
        // Keyed by SCENE, not by exporter: every call site constructs a fresh
        // `new SceneExporter(scene)`, so a per-instance queue would serialize
        // nothing. An export is a user gesture, not a hot path, so queueing
        // costs nothing real.
        const previous = WRITE_QUEUE.get(this.traceScene) ?? Promise.resolve();
        const run = previous.then(async () => {
            await awaitLayoutQuiescence(this.traceScene);
            return write();
        });
        // A failed export must not wedge every later one.
        WRITE_QUEUE.set(this.traceScene, run.then(() => undefined, () => undefined));
        return run;
    }

    /**
     * Export the whole scene to `.trace` (single JSON) or `.zip`
     * (models.json + info.json + assets).
     */
    async toTraceBlob(options: ExportOptions = {}): Promise<Blob> {
        return this.settled(() => this.exportTraceFormat(null, options));
    }

    /**
     * Export a subtree (a SceneObject and all its descendants) to `.trace` or `.zip`.
     */
    async subtreeToTraceBlob(sceneObject: SceneObject, options: ExportOptions = {}): Promise<Blob> {
        return this.settled(() => this.exportTraceFormat(sceneObject, options));
    }

    /**
     * Export the whole scene as a `.glz` **bundle** (spec §4): `scene.gltf` at
     * the archive root, the folder's files under `assets/`.
     */
    async toGlzBlob(options: BundleExportOptions = {}): Promise<Blob> {
        return this.settled(() => this.exportBundleFormat(null, options));
    }

    /**
     * Export a subtree as a `.glz` bundle.
     */
    async subtreeToGlzBlob(sceneObject: SceneObject, options: BundleExportOptions = {}): Promise<Blob> {
        return this.settled(() => this.exportBundleFormat({ roots: [sceneObject] }, options));
    }

    /**
     * @deprecated Renamed to {@link toGlzBlob}. `.tracex` is read-only from
     *   here (spec §5, #816); the bytes are the same archive, only the
     *   extension a caller should write changes.
     */
    async toTracexBlob(options: BundleExportOptions = {}): Promise<Blob> {
        return this.toGlzBlob(options);
    }

    /** @deprecated Renamed to {@link subtreeToGlzBlob} — see above. */
    async subtreeToTracexBlob(sceneObject: SceneObject, options: BundleExportOptions = {}): Promise<Blob> {
        return this.subtreeToGlzBlob(sceneObject, options);
    }

    /**
     * Export the scene as a standalone glTF 2.1 JSON document (`.gltf`).
     *
     * References only — there is no bundled `assets/` folder (that is
     * `.glz`, or a folder save), so every reference must already be a
     * hosted URL. The scene is validated first: a local path, `builtin:`
     * primitive or `blob:` URL throws before any bytes are written. Nothing is
     * baked here: a bake produces local files, and this form cannot carry them.
     */
    async toGltfBlob(options: DocumentExportOptions = {}): Promise<Blob> {
        return this.settled(() => this.writeGltf(options));
    }

    /**
     * @deprecated Renamed to {@link toGltfBlob} — the document is glTF 2.1 now,
     *   not glXF. Kept for one release so consumers can migrate.
     */
    async toGlxfBlob(options: DocumentExportOptions = {}): Promise<Blob> {
        return this.toGltfBlob(options);
    }

    /**
     * Export the scene as a **folder** (spec §2.1): the document plus the bytes
     * it names, keyed by the relative URI the document uses.
     *
     * This is the unit every save shares. A folder-mode save uploads
     * `files` through `POST /scenes/{id}/files` and then posts `document`; an
     * asset upload posts both together; `.glz` is `zip(exportFolder())`.
     * Nothing here packages, so a save that changed one file does not have to
     * build (or unpack) an archive to send it.
     *
     * `options.exclude` is how a session keeps **editor-only content** out of
     * the scene document (spec §2.9): the excluded subtrees, their behaviors,
     * their `files[]` entries and their `KHR_interactivity` contributions are
     * omitted, and the result is the document this scene would write if those
     * objects were not in it. A file a kept node also names still travels.
     * Nothing about the exclusion is persisted or on the wire.
     */
    async exportFolder(options: FolderExportOptions = {}): Promise<SceneFolder> {
        return this.settled(() => this.writeFolder(null, options));
    }

    /** Export a subtree as a folder — the promotion path (spec §3.5). */
    async subtreeToFolder(sceneObject: SceneObject, options: FolderExportOptions = {}): Promise<SceneFolder> {
        return this.settled(() => this.writeFolder({ roots: [sceneObject] }, options));
    }

    /**
     * "Save as asset": turn a selection into a library asset folder
     * (CONTENT_MODEL_SPEC §3.5).
     *
     * Four things this does that `subtreeToFolder` does not:
     *
     * 1. **Single root** (SCENE_ROOT_SPEC §4). A component asset has exactly
     *    one entry in `scenes[0].nodes[]`. A multi-object selection is grouped
     *    under one synthesized structural root, named for the asset. The group
     *    is created in the EXPORTED DOCUMENT; the live scene is untouched, so
     *    promotion stays a read and an editor keeps its own undo story. (An
     *    editor that wants the visible Figma-frame move runs its own group
     *    command first and promotes the single root it produced.)
     * 2. **Closure** (§3.2), computed on the manifest classes rather than on
     *    string shapes: relative entries — the scene's own folder files — are
     *    fetched and travel into the new asset folder (`destination: 'other'`);
     *    absolute entries stay references, because a library reference IS an
     *    identity and foreign streamable media is nobody's to copy.
     * 3. **The closure rule is enforced**, in ASSET mode: any URL under
     *    `trace-content/scenes/` is an offender. An asset may not borrow a
     *    scene's bytes — that scene's folder file dies with the scene.
     *    Offenders come back on the result instead of throwing, so the editor
     *    can show the author what to fix rather than swallowing a message.
     * 4. **It bakes**, per {@link bakesFor}: an asset does double duty, so it
     *    carries the shape the author sees.
     *
     * The promoted root's own behaviors ride on its node (`node.extras.trace`),
     * which is what SCENE_ROOT_SPEC §4 means by "the component root carries the
     * component's name, transform, and behaviors": place the asset and that
     * node becomes the stand-in the host receives, behaviors intact.
     * `scenes[0].extras.trace` is the document's own root-data slot and is
     * always written — empty for a promotion, because a subtree has no
     * scene-level root data to give it.
     *
     * @param subtree - one SceneObject, or the selection. A root that is a
     *   descendant of another root in the list is dropped (it already travels).
     */
    async promoteToAsset(
        subtree: SceneObject | readonly SceneObject[],
        options: PromoteToAssetOptions = {},
    ): Promise<PromotedAsset> {
        const roots: SceneObject[] = Array.isArray(subtree) ? [...(subtree as readonly SceneObject[])] : [subtree as SceneObject];
        if (roots.length === 0) {
            throw new Error('promoteToAsset: nothing selected — a promotion needs at least one object.');
        }

        const needsGroup = roots.length > 1 || options.alwaysGroup === true;
        const groupRootName = needsGroup ? (options.name ?? roots[0].name) : undefined;
        if (needsGroup) {
            Logger.log(
                `[SceneExporter] promoteToAsset: grouping ${roots.length} object(s) under "${groupRootName}" `
                + '(SCENE_ROOT_SPEC §4 single-root contract) — in the exported document only.',
            );
        }

        const folder = await this.settled(() => this.writeFolder(
            { roots, ...(groupRootName ? { groupRootName } : {}) },
            {
                ...(options.onProgress ? { onProgress: options.onProgress } : {}),
                ...(options.exclude ? { exclude: options.exclude } : {}),
                khrInteractivity: options.khrInteractivity,
                destination: 'other',
            },
        ));

        // Validated on what was actually WRITTEN, not on scene data: the
        // manifest is the document's dependency list, and the classes are what
        // the server's gate keys on.
        const manifest = await readManifestEntries(folder.document);
        const verdict = validateManifest(manifest, {
            ...(options.manifest ?? {}),
            rejectSceneFolderUrls: true,
        });
        if (!verdict.ok) {
            Logger.warn(
                '[SceneExporter] promoteToAsset: the asset closure rule (spec §3.2) refuses '
                + `${verdict.offenders.length} manifest entr(ies):\n${formatManifestOffenders(verdict.offenders)}`,
            );
        }

        return { document: folder.document, files: folder.files, offenders: verdict.offenders };
    }

    private async writeGltf(options: DocumentExportOptions = {}): Promise<Blob> {
        const sceneName = this.traceScene.sceneName;
        // Same cut as the folder path, at the same place: the records this
        // document is written from (CONTENT_MODEL_SPEC §2.9).
        const built = this.traceScene._buildDocumentExportData();
        const kept = applySubtreeExclusion(built.data, built.sceneObjects, options.exclude);
        const sceneData = kept.data;
        const liveByRecord = new Map(sceneData.map((record, index) => [record, kept.liveObjects[index]]));
        const propertySheets = collectExportPropertySheets(sceneData, record => liveByRecord.get(record) ?? undefined);

        const validation = validatePublishableScene(sceneData);
        if (!validation.ok) {
            throw new Error(validation.message);
        }
        assertPropertySheetAssetsHosted(propertySheets);

        // A standalone document references assets where they are hosted, so the
        // legacy GLB-root bake the bundled formats apply has nothing to write
        // to. Say so rather than emitting a document that renders wrong
        // everywhere — the fix is to re-host the affected models with their
        // roots already at identity.
        const legacyRoots = this.collectResetRootAssets(null, options.exclude);
        if (legacyRoots.size > 0) {
            Logger.warn(
                `[SceneExporter] ${legacyRoots.size} model(s) in this scene had their GLB root discarded on load `
                + '(pre-v2 .trace). A standalone .gltf cannot carry that, so those models will be '
                + `double-transformed by any viewer: ${[...legacyRoots].join(', ')}`,
            );
        }

        const { BundleExporter } = await import('../loaders/formats/tracex/tracex-exporter');
        const exporter = new BundleExporter({
            sceneName,
            includeAssets: false,
            preserveExternalUris: true,
            propertySheets,
            khrInteractivity: options.khrInteractivity,
            // This IS the scene's document, so it carries what the opened one
            // carried and this writer does not author (issue #834).
            foreignDocument: this.traceScene.foreignDocument,
        });

        const folder = await exporter.buildFolder(sceneData);
        reportExportWarnings('.gltf', sceneName, folder.warnings);

        const json = JSON.stringify(folder.document, null, 2);
        return new Blob([json], { type: GLTF_JSON_MIME });
    }

    /**
     * Export a single SceneObject as a `.glb` Blob.
     */
    async objectToGlbBlob(sceneObject: SceneObject): Promise<Blob> {
        return this.settled(() => this.writeObjectGlb(sceneObject));
    }

    private async writeObjectGlb(sceneObject: SceneObject): Promise<Blob> {
        // An ASSET (see `bakesFor`), so it carries what the user SEES at
        // Base — morph influences blended into vertices, triplanar
        // projections rewritten into real UVs, scale-fallback sizing folded
        // in. Base, because a .glb has no states to leave it for: a state
        // previewed at export time must not become the object's only look
        // (showBaseForBake). Every asset does double duty: a dumb glTF
        // consumer renders these bytes, and the editor re-opens the original
        // through the provenance saved beside them. The live scene is
        // restored untouched below.
        //
        // Every object is posed, not only the baked ones: the file serializes
        // the whole subtree's materials.
        const subtree = [sceneObject, ...sceneObject.getDescendants()];
        const showLive: Array<() => Promise<void>> = [];
        const materialDeadline = Date.now() + MATERIAL_SETTLE_TIMEOUT_MS;
        try {
            for (const so of subtree) showLive.push(await showBaseForBake(so, materialDeadline));
            return await this.writePosedObjectGlb(sceneObject, subtree);
        } finally {
            for (const restore of showLive.reverse()) await restore();
        }
    }

    /** {@link writeObjectGlb} once the subtree shows Base: plan, bake, serialize, restore. */
    private async writePosedObjectGlb(sceneObject: SceneObject, subtree: SceneObject[]): Promise<Blob> {
        const root = sceneObject.transform;
        const fileName = `${sceneObject.name}.glb`;

        // Nested smart objects are a designed-for shape (tv#638 receivers
        // compose inside smart objects), and every plan here bakes before any
        // restores. Two plans holding one mesh is therefore destructive: the
        // second snapshots the first's THROWAWAY geometry and its restore
        // disposes the real thing, leaving the live mesh permanently baked
        // and morphless. Plans are built deepest-first through one claim set,
        // so each mesh is baked exactly once, by the object that owns it.
        const claimed = new Set<Mesh>();
        const bakes: LiveNodeBake[] = [];
        for (const so of [...subtree].reverse()) {
            if (!objectNeedsBake(so)) continue;
            const plan = new LiveNodeBake(so, claimed);
            if (plan.hasWork) bakes.push(plan);
        }
        // The bake loop sits INSIDE the try: a bake that throws partway
        // through the list (or partway through one node) must still restore
        // every snapshot already taken — restore() is tolerant of partial
        // bakes, serialization state is not.
        try {
            for (const plan of bakes) plan.bake();

            // Editor helper meshes (Text/collider pickers) live inside object
            // subtrees, so a raw descendant walk would serialize them as
            // phantom slabs. Excluding them from the set BEFORE the animation
            // scan below also blocks any track that targets a helper node.
            // Unlike the per-record bake this keeps nested SceneObjects: one
            // object exported as one file IS the whole subtree.
            const helpers: Node[] = [];
            const nodesToExport = new Set<Node>(
                [root, ...root.getDescendants(false)].filter((node) => {
                    if (!isEditorHelperMesh(node)) return true;
                    helpers.push(node);
                    return false;
                }),
            );
            warnHelperMeshExcluded(`SceneExporter ${fileName}`, helpers);

            Logger.log(`Exporting GLB for ${sceneObject.name} with ${nodesToExport.size} nodes`);

            return await serializeNodesToGlb(
                this.traceScene.getBabylonScene(),
                fileName,
                nodesToExport,
            );
        } finally {
            // Reverse of bake order: restores unwind the mutations in the
            // order they were made, so nothing hands a mesh back a geometry
            // a later plan is still standing on.
            for (const plan of [...bakes].reverse()) plan.restore();
        }
    }

    // ----- internals -----

    private async exportTraceFormat(
        subtreeRoot: SceneObject | null,
        options: ExportOptions,
    ): Promise<Blob> {
        const { zipFormat = 'auto' } = options;

        const targets = subtreeRoot
            ? [subtreeRoot, ...subtreeRoot.getDescendants()]
            : this.traceScene.getSceneObjects();
        for (const so of targets) {
            so.refreshSceneData?.();
        }

        const sceneName = subtreeRoot ? subtreeRoot.name : this.traceScene.sceneName;
        let infoFile = this.traceScene.sourceInfoFile;

        if (zipFormat === 'trace') {
            infoFile = undefined;
        } else if (zipFormat === 'zip' && !infoFile) {
            // Synthesize a minimal info.json so the export uses models.json + info.json format
            infoFile = { traces: [] };
        }

        // Which artifact this actually writes, resolved BEFORE anything reads
        // it. `'auto'` follows the scene's provenance, so deriving the bake
        // from `infoFile` alone made the same "Export as .trace" gesture bake
        // in a fresh scene and not in one loaded from a legacy `.zip`.
        const resolvedFormat: 'trace' | 'zip' = infoFile ? 'zip' : 'trace';

        const bundleExternalAssets = !infoFile;
        // `exclude` reaches the LEGACY writers too (spec §2.9): they capture
        // the live tree like every other writer, so a session with editor-only
        // content has to be able to say so here as well. Without it a host had
        // to detach the subtree around the write — mutating the live scene
        // around a read (TraceFrontend #2383).
        const { onProgress, imageMarker, exclude } = options;
        const exporter = new TraceExport();
        return exporter.exportScene(
            this.traceScene.getBabylonScene(),
            sceneName,
            infoFile,
            this.traceScene,
            subtreeRoot
                ? { bundleExternalAssets, subtreeRoot, imageMarker, onProgress, exclude, bake: bakesFor(resolvedFormat) }
                : { bundleExternalAssets, imageMarker, onProgress, exclude, bake: bakesFor(resolvedFormat) },
        );
    }

    /**
     * The `src` paths whose loaded models had their GLB root discarded.
     *
     * The decision is made once, per node, by the scene-format plugin and
     * lives on the SceneObject — it is deliberately not serialized, because
     * it describes how a legacy file was interpreted, not what the scene is.
     * Export is where that interpretation has to be made permanent.
     */
    private collectResetRootAssets(
        subtree: SubtreeScope | null,
        exclude: ExcludedSubtrees | undefined,
    ): Set<string> {
        // The same objects the document is written from: an excluded subtree's
        // legacy model is neither warned about nor baked unless a kept node
        // names the same file (spec §2.9).
        const scope = withoutExcluded(
            subtree
                ? subtree.roots.flatMap(root => [root, ...root.getDescendants()])
                : this.traceScene.getSceneObjects(),
            exclude,
        );
        const paths = new Set<string>();
        for (const object of scope) {
            if (object._resetAssetTransform && object.src) paths.add(object.src);
        }
        return paths;
    }

    /**
     * Everything both packagers share: the capture, the bakes, and an exporter
     * wired to resolve the prepared bytes. Split out so `zip(folder)` and a
     * folder save cannot drift — they are the same export, packaged twice.
     */
    private async prepareFolderExport(
        subtree: SubtreeScope | null,
        options: BundleExportOptions = {},
        /**
         * What to do with a folder file the scene loaded over the network and
         * never changed. A folder save NAMES it and sends nothing — the bytes
         * are already at the destination (spec §2.2). An archive has no
         * destination folder to lean on, so it has to fetch them: a `.glz`
         * download of a loaded scene must be complete or it is not a download.
         */
        existingFolderFiles: 'name' | 'fetch' = 'name',
        /**
         * What to do when a file the scene claims cannot be produced at all.
         *
         * A SAVE must not drop one (`'throw'`): a document naming a file the
         * destination folder does not hold is a scene that loads empty. The
         * archive writers keep `'drop'` (tv#717) — a `.glz` download of a
         * legacy scene whose bytes are genuinely gone is still worth having,
         * as long as its manifest does not name what the ZIP lacks.
         */
        unresolvedLocalAssets: 'drop' | 'throw' = existingFolderFiles === 'fetch' ? 'drop' : 'throw',
        /**
         * Where this export is being written to, when the caller knows — see
         * {@link FolderExportOptions.folderUrl}. Archive packagers pass
         * nothing: a `.glz` has no destination folder to name files in.
         */
        destinationFolderUrl?: string,
    ): Promise<{
        exporter: import('../loaders/formats/tracex/tracex-exporter').BundleExporter;
        content: import('../scenedata/sceneobjectdata').SceneObjectData[];
        sceneName: string;
    }> {
        const babylonScene = this.traceScene.getBabylonScene();
        const sceneName = subtree
            ? (subtree.groupRootName ?? subtree.roots[0]?.name ?? this.traceScene.sceneName)
            : this.traceScene.sceneName;
        const resources = this.traceScene.resources;

        // One preparation, two packagers: TraceExport runs the same capture,
        // Bevel/deformed-geometry bakes (morphs, corner radii, scale folds,
        // UV re-projection), primitive generation, and helper-mesh filtering
        // the `.trace` zip gets — this writer only packages differently.
        // bundleExternalAssets:false keeps remote URLs as-is (this format's
        // existing semantics: the resolver below fetches what it needs);
        // site-absolute assets still bundle inside the preparation.
        const prep = new TraceExport();
        const { content, assets, propertySheets, changedPropertySheets } = await prep.prepareSceneContent(
            babylonScene,
            sceneName,
            this.traceScene,
            subtree
                ? {
                    bundleExternalAssets: false,
                    subtreeRoots: subtree.roots,
                    ...(subtree.groupRootName ? { groupRootName: subtree.groupRootName } : {}),
                    ...(options.exclude ? { exclude: options.exclude } : {}),
                    bake: bakesFor('glz'),
                    reuseUnchangedBakes: { folderUrl: destinationFolderUrl },
                }
                : {
                    bundleExternalAssets: false,
                    ...(options.exclude ? { exclude: options.exclude } : {}),
                    bake: bakesFor('glz'),
                    reuseUnchangedBakes: { folderUrl: destinationFolderUrl },
                },
        );

        // ── The legacy → 2.1 conversion (SCENE_ROOT_SPEC §2.3) ─────────────
        // A scene OPENED from a legacy format and written as a 2.1 document is
        // crossing the boundary this rule is enforced at. Reference nodes carry
        // the referenced root's behaviors, copied there by the old import; the
        // copies are dropped once, here, as the document is written. Nothing
        // happens for a scene that was already 2.1 — its import built the right
        // tree — and nothing ever happens on read.
        if (isLegacySceneFormat(this.traceScene.sceneSourceFormat)) {
            const dropped = dropImportEraReferenceCopies(content, record => prep.liveObjectFor(record));
            if (dropped > 0) {
                Logger.log(
                    `[SceneExporter] legacy → 2.1 conversion: dropped ${dropped} import-era reference `
                    + `copy/copies from "${sceneName}" (SCENE_ROOT_SPEC §2.3)`,
                );
            }
        }

        // Assets whose GLB root the runtime discarded (legacy pre-v2 `.trace`,
        // where Unity had already baked that root into the scene transform).
        // A bundle preserves an asset's own transform, so the root has to be
        // baked out here or the content double-transforms on re-import — and
        // in any other glTF viewer too. See export/glbRootBake.ts.
        const resetRootAssets = this.collectResetRootAssets(subtree, options.exclude);
        const legacyKeys = new Set([...resetRootAssets].map(preparedAssetKey));
        // Prepared bytes that replace a file the folder may already hold:
        // regenerated primitive geometry and sheets with repointed dependencies.
        const regeneratedKeys = new Set(
            [...Object.values(PRIMITIVE_FILENAMES).filter((filename) => assets.has(filename)), ...changedPropertySheets],
        );
        for (const [key, blob] of assets) {
            const original = resources.getAsset(`./assets/${preparedAssetKey(key)}`);
            if (fileExtensionFromUri(key) === 'gltf' && original && blob !== original) regeneratedKeys.add(preparedAssetKey(key));
        }
        // TraceExport reconstructs uncached sheets for its legacy archives.
        // This packager can fetch original folder bytes, preserving unknown
        // envelope fields unless a dependency rewrite changed the sheet.
        for (const path of propertySheets.keys()) {
            const key = preparedAssetKey(path);
            if (!changedPropertySheets.has(key) && !resources.getAsset(path) && resources.resolveAssetUrl(path)) {
                assets.delete(key);
            }
        }
        const { bakeGlbRootIdentity } = resetRootAssets.size > 0
            ? await import('./glbRootBake')
            : { bakeGlbRootIdentity: () => undefined };

        const { BundleExporter } = await import('../loaders/formats/tracex/tracex-exporter');
        const exporter = new BundleExporter({
            sceneName,
            manifest: options.manifest,
            propertySheets,
            assetRequiresBake: path => resetRootAssets.has(path),
            includeAssets: true,
            // `files[k].name` (spec §2.1): the original human name of each
            // folder file, so a load → save round trip preserves it and the
            // bundle writer can name archive entries with it. The
            // ResourceManager is where the three sources converge — the
            // `POST /files` echo, the dropped `File.name`, and the loaded
            // document's own `files[k].name`. Nothing resolves by it.
            assetName: (path: string) => resources.getAssetName(path),
            onProgress: options.onProgress,
            khrInteractivity: options.khrInteractivity,
            // The opened document's foreign half, written back on save
            // (issue #834). A SUBTREE export — promotion — is a NEW document
            // with its own provenance, so it takes none of it; the node-level
            // bags travel with the objects and are kept either way.
            ...(subtree ? {} : { foreignDocument: this.traceScene.foreignDocument }),
            // A SAVE must not drop a file the scene claims as its own; an
            // archive download may. Decided by the caller — see the parameter.
            unresolvedLocalAssets,
            assetResolver: async (path: string) => {
                // A legacy asset is keyed by the object's authored src; refs
                // reach here in document form. Both go through
                // `preparedAssetKey` so the `./assets/` prefix cannot split
                // them and a legacy root slip through un-baked.
                const prepareAsset = async (bytes: Uint8Array): Promise<Uint8Array> => {
                    const baked = legacyKeys.has(preparedAssetKey(path)) ? bakeGlbRootIdentity(bytes) ?? bytes : bytes;
                    if (fileExtensionFromUri(path) !== 'gltf') return baked;
                    try {
                        const blob = await inlineGltfExternalAssets(path, new Blob([baked.slice().buffer]), resources);
                        return new Uint8Array(await blob.arrayBuffer());
                    } catch (error) {
                        if (unresolvedLocalAssets === 'throw') throw error;
                        Logger.warn(`[SceneExporter] ${error instanceof Error ? error.message : String(error)}; preserving the original model`);
                        return baked;
                    }
                };

                // ── The destination already holds this file (spec §2.2) ────
                // Asked FIRST — before the prepared set, before the live blob
                // — because for a LEAF opened from its own folder both of
                // those answer. The editor downloaded the bytes in order to
                // show them, so it is holding a copy of a file the
                // destination already has, and resolving them re-uploads
                // exactly what wrap-on-first-edit exists not to re-upload.
                // The later "already in the folder" answer cannot be moved up
                // to cover this: it reads `assetUrls`, which a leaf has no
                // entry in, and it is last precisely because a local blob
                // should win when the two disagree.
                //
                // Sound in the presence of the bakes (which DO run here —
                // `bakesFor('glz')`) because a bake never lands under the name
                // it read: the Bevel and deformed-geometry passes deliver by
                // rewriting `record.src` to `bevel_…`/`morphbake_…`, names no
                // folder holds, so a baked file's source URL is not in the
                // destination and it uploads. Legacy GLB-root bakes, regenerated
                // Shapes and rewritten sheets can retain their old name. Their
                // changed bytes must not be short-circuited.
                const key = preparedAssetKey(path);
                if (!legacyKeys.has(key) && !regeneratedKeys.has(key)) {
                    const source = resources.resolveAssetUrl(path) ?? resources.getAssetSourceUrl(path);
                    if (destinationFolderUrl && source?.startsWith(destinationFolderUrl)) return ASSET_ALREADY_IN_FOLDER;
                    // Loaded sheets have parsed snapshots even when their bytes
                    // stayed in the folder. An unchanged snapshot is not an upload.
                    if (propertySheets.has(path) && existingFolderFiles === 'name'
                        && !resources.getAsset(path) && resources.resolveAssetUrl(path)) return ASSET_ALREADY_IN_FOLDER;
                }
                // Everything the preparation produced or bundled — baked
                // geometry, generated primitives, site-absolute downloads —
                // resolves from the prepared set first, under the key it was
                // stored as (`preparedAssetKey`; refs differ from keys by the
                // `./assets/` prefix). No basename fallback: a nested scene's
                // subassets are re-homed to `assets/<sceneFolder>/<name>`
                // precisely so its `panel.glb` stays distinct from another
                // scene's, and a basename probe would hand one the other's
                // bytes. A miss falls through to the live scene's resources
                // below, which resolve the full path.
                //
                // The legacy root bake applies to prepared bytes too: the
                // original a baked object's `bakedFrom` points at IS the
                // legacy asset, and v2 would re-apply its root on re-open. The
                // morph-baked GLB itself is serialized from the live nodes,
                // which already carry the discarded root, and is keyed apart.
                const prepared = assets.get(preparedAssetKey(path));
                if (prepared) {
                    return prepareAsset(new Uint8Array(await prepared.arrayBuffer()));
                }
                const blob = resources.getAsset(path);
                if (blob) {
                    return prepareAsset(new Uint8Array(await blob.arrayBuffer()));
                }
                const loadedSheet = propertySheets.get(path);
                if (loadedSheet && !resources.resolveAssetUrl(path)) return new TextEncoder().encode(JSON.stringify(loadedSheet));
                // Site-absolute srcs (`/generated/<hash>.jpg` from image
                // generation) are served by the authoring origin. They are
                // cached above when the session that created them registered
                // the blob, but not when the scene was loaded fresh — hence the
                // fetch rather than a hard failure. Without it the archive
                // references media it does not contain and images vanish in any
                // other viewer.
                if (isSiteAbsolutePath(path)) {
                    try {
                        const res = await fetch(path);
                        if (res.ok) {
                            const body = await res.arrayBuffer();
                            // A dev server's SPA fallback answers a missing path
                            // with a 200 page. That is a miss, like the 404 below,
                            // not bytes to name as the asset.
                            if (!isWebPageNotAsset(path, res.headers.get('content-type'), body)) {
                                return await prepareAsset(new Uint8Array(body));
                            }
                            Logger.warn(`[SceneExporter] bundle asset fetch failed (a web page, not an asset): ${path}`);
                            return null;
                        }
                        Logger.warn(`[SceneExporter] bundle asset fetch failed (${res.status}): ${path}`);
                    } catch (err) {
                        Logger.warn(`[SceneExporter] bundle asset fetch error: ${path}`, err);
                    }
                    return null;
                }

                // A pre-v2 loader discarded this GLB's own root transform. Keeping its
                // original bytes would apply that transform again on reopen. Only this
                // compatibility conversion needs to fetch a hosted GLB; ordinary links do not.
                if (resetRootAssets.has(path) && isAbsoluteHttpUrl(path)) {
                    const response = await fetch(path);
                    if (!response.ok) throw new Error(`Legacy GLB root bake failed for ${path}: HTTP ${response.status}`);
                    const bytes = new Uint8Array(await response.arrayBuffer());
                    return bakeGlbRootIdentity(bytes, { requireValid: true }) ?? bytes;
                }

                // A FOLDER FILE loaded over the network. It holds no blob here
                // — the ResourceManager knows it as a URL mapping, registered
                // when the document named it (spec §2.1) — and that is not a
                // miss: the file is in the folder this document is about to be
                // written back into. Before this, the miss fell through to
                // `null`, the writer dropped the entry, and re-saving a folder
                // scene silently shipped a node with no model.
                const folderUrl = resources.resolveAssetUrl(path);
                if (folderUrl) {
                    if (existingFolderFiles === 'name') return ASSET_ALREADY_IN_FOLDER;
                    // An archive has to carry it, so go and get it. A failure
                    // here is a real failure: returning null would make the
                    // writer throw rather than write a manifest naming bytes
                    // the archive does not have.
                    const res = await fetch(folderUrl);
                    if (!res.ok) {
                        throw new Error(
                            `Could not fetch folder file "${path}" from ${folderUrl} `
                            + `for the archive: ${res.status} ${res.statusText}`,
                        );
                    }
                    return prepareAsset(new Uint8Array(await res.arrayBuffer()));
                }

                // A strict save owns hosted scene archive bytes (a live reference
                // was already excluded by the writer), and also carries a hosted
                // single-file asset (model, splat, image, video or audio): a
                // library/catalog file such as a Creator .zip's absolute blob URL.
                // Same download path as loading, without retaining another
                // raw-byte cache; an unreachable file blocks the save. A hosted
                // .gltf is several files, so it is deliberately not copied.
                if (unresolvedLocalAssets === 'throw' && isAbsoluteHttpUrl(path)
                    && content.some(node => node.src === path)) {
                    const extension = fileExtensionFromUri(path);
                    const isArchive = getSceneFormatConfig(extension)?.dataType === 'arraybuffer';
                    // .gltf is excluded because it is several files. Splats take this path
                    // too, untested here: their loader needs the network in the test setup.
                    const isPlainFile = extension !== 'gltf' && DROPPABLE_ASSET_EXTENSIONS.has(extension);
                    if (isArchive || isPlainFile) {
                        const { blob } = await this.traceScene.fetchAssetWithProgress(path);
                        const bytes = new Uint8Array(await blob.arrayBuffer());
                        if (this.traceScene.getBabylonScene().isDisposed) throw disposedError();
                        if (isArchive && zipEntryNames(bytes) === undefined) throw new Error(`Hosted scene archive is not a complete ZIP: ${path}`);
                        return bytes;
                    }
                }

                return null;
            },
        });

        return { exporter, content, sceneName };
    }

    /** `zip(folder)` — the `.glz` bundle (spec §4). */
    private async exportBundleFormat(subtree: SubtreeScope | null, options: BundleExportOptions = {}): Promise<Blob> {
        const { exporter, content, sceneName } = await this.prepareFolderExport(subtree, options, 'fetch');

        const result = await exporter.export(content);
        if (!result.success) {
            throw new Error(`Bundle export failed: ${result.error}`);
        }
        reportExportWarnings('.glz', sceneName, result.warnings);

        return new Blob([result.data.buffer as ArrayBuffer], { type: 'application/zip' });
    }

    /** The folder itself, unpackaged — see {@link SceneFolder}. */
    private async writeFolder(subtree: SubtreeScope | null, options: FolderExportOptions = {}): Promise<SceneFolder> {
        // `'other'` reuses the archive path's fetch — a destination folder that
        // does not already hold the bytes is the same problem a ZIP has — but
        // NOT its tolerance for a file it could not get: this is still a save,
        // and a save may not silently drop a file the document names.
        const destination: FolderDestination = options.destination ?? 'same';
        const { exporter, content, sceneName } = await this.prepareFolderExport(
            subtree,
            options,
            destination === 'other' ? 'fetch' : 'name',
            'throw',
            // Only a save back into the SAME folder can name what is already
            // there; `'other'` is a copy, and every file has to travel.
            destination === 'same' ? options.folderUrl : undefined,
        );

        const folder = await exporter.buildFolder(content);
        reportExportWarnings('folder', sceneName, folder.warnings);

        if (options.manifest) {
            const verdict = validateManifest(folder.document.files ?? [], options.manifest);
            if (!verdict.ok) {
                throw new Error(
                    `This scene references files the content model does not allow:\n`
                    + formatManifestOffenders(verdict.offenders),
                );
            }
        }

        const files = new Map<string, Blob>();
        for (const [uri, bytes] of folder.files) {
            files.set(uri, new Blob([bytes.buffer as ArrayBuffer]));
        }
        // Files this session already stored in the destination, unchanged
        // since, are named rather than sent (spec §2.2).
        const folderUrl = destination === 'same' ? options.folderUrl : undefined;
        const resources = this.traceScene.resources;
        const document = folderUrl && resources.hasStoredFiles(folderUrl)
            ? await nameStoredFiles(folder.document, files, folder.existing, folderUrl, resources)
            : folder.document;
        return {
            document: new Blob([JSON.stringify(document, null, 2)], { type: GLTF_JSON_MIME }),
            files,
            existing: folder.existing,
        };
    }
}
